"use client";

import { X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useCart } from "@/hooks/useCart";
import { pushToDataLayer } from "@/lib/analytics/gtm";
import { parseCouponParam } from "@/lib/coupons/couponPromo";

// Link de campanha/recuperação com ?cupom=CODIGO (2026-10-25): guarda o
// código, tira o parâmetro da URL e aplica assim que o carrinho tiver
// produtos. Quem valida é o servidor (primeira compra, datas, limites).

const STORAGE_KEY = "persi_pending_coupon";

function readPending(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writePending(code: string | null) {
  try {
    if (code) window.localStorage.setItem(STORAGE_KEY, code);
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // sem armazenamento: aplica só nesta página
  }
}

export function CouponFromUrl() {
  const { cart, isHydrated, supportsCoupons, applyCoupon } = useCart();
  const [pending, setPending] = useState<string | null>(null);
  const [toast, setToast] = useState<{ text: string; ok: boolean } | null>(null);
  const applying = useRef(false);
  const notifiedEmpty = useRef(false);

  // 1) Lê ?cupom= uma vez e limpa a URL.
  useEffect(() => {
    const fromUrl = parseCouponParam(window.location.search);
    if (fromUrl) {
      writePending(fromUrl);
      const url = new URL(window.location.href);
      url.searchParams.delete("cupom");
      window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
    }
    const code = fromUrl ?? readPending();
    void Promise.resolve().then(() => setPending(code));
  }, []);

  // 2) Aplica quando há produtos no carrinho.
  const hasItems = Boolean(cart?.items.length);
  const alreadyApplied = cart?.coupons.some((coupon) => coupon.code.toUpperCase() === pending) ?? false;
  useEffect(() => {
    if (!pending || !isHydrated || !supportsCoupons || applying.current) return;
    if (alreadyApplied) {
      writePending(null);
      void Promise.resolve().then(() => setPending(null));
      return;
    }
    if (!hasItems) {
      if (notifiedEmpty.current) return;
      notifiedEmpty.current = true;
      void Promise.resolve().then(() => setToast({ text: `Cupom ${pending} guardado: ele entra quando você adicionar produtos ao carrinho.`, ok: true }));
      return;
    }
    applying.current = true;
    void applyCoupon(pending).then((result) => {
      applying.current = false;
      writePending(null);
      setPending(null);
      setToast({ text: result.success ? `Cupom ${pending} aplicado!` : result.message || `Não foi possível aplicar o cupom ${pending}.`, ok: result.success });
      pushToDataLayer({ event: "coupon_url_applied", cupom: pending, sucesso: result.success });
    });
  }, [pending, isHydrated, supportsCoupons, hasItems, alreadyApplied, applyCoupon]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 6000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  if (!toast) return null;
  return (
    <div role="status" className="fixed inset-x-4 bottom-24 z-[60] mx-auto flex max-w-md items-start gap-3 rounded-xl bg-slate-900 px-4 py-3 text-sm text-white shadow-lg sm:bottom-6">
      <span aria-hidden="true">{toast.ok ? "🎟️" : "⚠️"}</span>
      <p className="flex-1">{toast.text}</p>
      <button type="button" onClick={() => setToast(null)} aria-label="Fechar aviso" className="-m-2 p-2 text-white/70 hover:text-white">
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}
